import React from 'react';
import { motion } from 'framer-motion';
import { Badge } from './ui/badge';

const regions = [
  { name: 'Turkey', code: 'TR', flag: '🇹🇷' },
  { name: 'Thailand', code: 'TH', flag: '🇹🇭' },
  { name: 'UAE', code: 'AE', flag: '🇦🇪' },
  { name: 'Japan', code: 'JP', flag: '🇯🇵' },
  { name: 'Singapore', code: 'SG', flag: '🇸🇬' },
  { name: 'South Korea', code: 'KR', flag: '🇰🇷' },
  { name: 'Vietnam', code: 'VN', flag: '🇻🇳' },
  { name: 'China', code: 'CN', flag: '🇨🇳' },
  { name: 'Brazil', code: 'BR', flag: '🇧🇷' },
  { name: 'India', code: 'IN', flag: '🇮🇳' },
  { name: 'Indonesia', code: 'ID', flag: '🇮🇩' },
  { name: 'Malaysia', code: 'MY', flag: '🇲🇾' },
  { name: 'Philippines', code: 'PH', flag: '🇵🇭' },
  { name: 'Sri Lanka', code: 'LK', flag: '🇱🇰' },
  { name: 'Egypt', code: 'EG', flag: '🇪🇬' },
  { name: 'UK', code: 'GB', flag: '🇬🇧' },
  { name: 'Germany', code: 'DE', flag: '🇩🇪' },
  { name: 'USA', code: 'US', flag: '🇺🇸' },
];

export const GlobalCoverage = () => {
  return (
    <section id="coverage" className="py-16 sm:py-20 bg-white" data-testid="coverage-section">
      <div className="container-main">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left: Copy + Stats */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <p className="font-mono-accent text-xs font-medium uppercase tracking-[0.18em] text-[#7C3AED] mb-3">
              Global coverage
            </p>
            <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[#0B0B10] mb-4">
              QR payments available in 18+ countries
            </h2>
            <p className="text-base text-[#64647A] max-w-lg mb-8 leading-relaxed">
              Our network spans across Asia, Middle East, Europe, and the Americas. Direct transfers reach 180+ countries worldwide, with QR code payments accepted at millions of point-of-sale locations.
            </p>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 mb-6">
              <div>
                <p className="stat-number text-3xl">180+</p>
                <p className="text-xs text-[#64647A] mt-1">Countries for transfers</p>
              </div>
              <div>
                <p className="stat-number text-3xl">18</p>
                <p className="text-xs text-[#64647A] mt-1">QR payment markets</p>
              </div>
              <div>
                <p className="stat-number text-3xl">24/7</p>
                <p className="text-xs text-[#64647A] mt-1">Transaction processing</p>
              </div>
            </div>
          </motion.div>

          {/* Right: Flag/Region Grid */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.15 }}
          >
            <div className="bg-white border border-[#E7E7EF] rounded-2xl p-6 shadow-sm">
              <div className="relative mb-4 rounded-xl overflow-hidden" style={{ height: '160px', background: '#0B0B10' }}>
                {/* Abstract world map visualization */}
                <svg viewBox="0 0 400 160" className="w-full h-full" style={{ opacity: 0.6 }}>
                  {/* Grid dots representing global network */}
                  {Array.from({ length: 60 }).map((_, i) => (
                    <circle
                      key={i}
                      cx={30 + (i % 12) * 30}
                      cy={20 + Math.floor(i / 12) * 28}
                      r={Math.random() > 0.7 ? 3 : 1.5}
                      fill={Math.random() > 0.5 ? '#7C3AED' : '#A78BFA'}
                      opacity={0.3 + Math.random() * 0.7}
                    />
                  ))}
                  {/* Connection lines */}
                  <line x1="60" y1="48" x2="150" y2="76" stroke="#7C3AED" strokeWidth="0.5" opacity="0.3" />
                  <line x1="150" y1="76" x2="270" y2="48" stroke="#7C3AED" strokeWidth="0.5" opacity="0.3" />
                  <line x1="270" y1="48" x2="330" y2="76" stroke="#7C3AED" strokeWidth="0.5" opacity="0.3" />
                  <line x1="90" y1="104" x2="210" y2="76" stroke="#A78BFA" strokeWidth="0.5" opacity="0.3" />
                  <line x1="210" y1="76" x2="330" y2="104" stroke="#A78BFA" strokeWidth="0.5" opacity="0.3" />
                </svg>
                {/* Glow effect */}
                <div className="absolute inset-0" style={{ background: 'radial-gradient(circle at 50% 50%, rgba(124,58,237,0.15), transparent 70%)' }} />
              </div>

              <p className="text-xs font-medium text-[#64647A] mb-3 font-mono-accent uppercase tracking-wider">QR payment regions</p>
              <div className="flex flex-wrap gap-2">
                {regions.map((region) => (
                  <Badge
                    key={region.code}
                    variant="secondary"
                    data-testid={`coverage-region-chip-${region.code}`}
                    className="bg-[#F7F7FB] text-[#2A2A36] border-0 rounded-lg px-2.5 py-1.5 text-xs hover:bg-[#EDE9FE] transition-colors cursor-default"
                  >
                    <span className="mr-1">{region.flag}</span>
                    <span className="font-mono-accent text-xs">{region.code}</span>
                  </Badge>
                ))}
              </div>
              <p className="text-xs text-[#A78BFA] mt-3">Coverage expanding — new regions added quarterly</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
